import { Edit as EditIcon, WarningAmber } from "@mui/icons-material";
import { FC, useContext, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  CellSimple,
  DragIconReplacement,
  FramerTooltip,
  RideStationInOutIcon,
} from "@ryde/controls";
import { CourseStationContext, useAreaTitle } from "@ryde/redux";
import {
  combineStrings,
  coursePotentialPassengersAtom,
  DirectionEnum,
  getCourseStationDuration,
  getRealStationListOrdinal,
  getWaypointCategoryVisual,
  IPossiblePassenger,
  isCourseStationDragOrderable,
  isLocationInfoWithValidMapCoordinates,
  isStationRouteWaypointStation,
  NO_VALUE_DISPLAY,
  RideStationType,
  SINGLE_KEY,
  useDrawers,
  useIsMobile,
  useSystemParams,
  useTimezone,
  ZERO_TIME_HHmm,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { StationRouteWaypointRowBadge } from "../../../station-routes";
import { PotentialPassengersTooltip } from "./potential.passengers.tooltip";
import { StationCourseTimeEdit } from "./station.course.time-edit";
import { StationRowButtonsCourse } from "./station.row.buttons.course";
import {
  CourseStationRowStyled,
  CourseStationWarningContentWrapperStyled,
  StationBoxCenterStyled,
  StationBoxLeftStyled,
  StationBoxRightStyled,
} from "./stations.course.styled";

export const StationRowCourse: FC = () => {
  const {
    stationIndex = 0,
    station,
    courseDto,
    attributes,
    listeners,
    isFirstStation,
    isLastStation,
  } = useContext(CourseStationContext);
  const watchedCourseDirection = useWatch({ name: "metadata.direction" });

  const { timezone } = useTimezone();
  const coursePotentialPassengers = useRecoilValue(
    coursePotentialPassengersAtom,
  );

  const {
    id: stationId = 0,
    name: stationName = "",
    areaId,
    timeArrival,
  } = station || {};
  const { isMobile } = useIsMobile();
  const { enableCoursePotentialPassengersCount } = useSystemParams();
  const isCoursePotentialPassengersCountEnabled =
    enableCoursePotentialPassengersCount !== false;
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { title: areaTitle } = useAreaTitle(areaId);

  const stationFullName = combineStrings(
    stationName,
    station?.locationInfo?.fullAddress,
    " (",
    ")",
  );

  const isSingleStation = useMemo(() => {
    if (isLastStation && isFirstStation) {
      return true;
    }

    return false;
  }, [isFirstStation, isLastStation]);

  const duration = useMemo(() => {
    const durationMin = getCourseStationDuration(
      courseDto,
      stationIndex,
      timezone,
    );

    if (durationMin === undefined || Number.isNaN(durationMin)) {
      return NO_VALUE_DISPLAY;
    }

    return `${durationMin} ${t("general.MIN")}`;
  }, [courseDto, stationIndex, t, timezone]);

  const handleOpenStationClick = useEvent(() => {
    openEditFormDrawer({
      entity: "station",
      id: stationId,
    });
  });

  const isWaypoint = isStationRouteWaypointStation(station);
  const waypointVisual = getWaypointCategoryVisual(station?.waypointCategory);
  const realStationOrdinal = getRealStationListOrdinal({
    station,
    stations: courseDto?.metadata?.stationsInfo,
    listIndex: stationIndex,
  });

  const timeEditAllowed = !isWaypoint;
  const isCourseStationDraggable = isCourseStationDragOrderable({
    station,
    isFirstStation,
    isLastStation,
    isSingleStation,
    stations: courseDto?.metadata?.stationsInfo,
  });

  const potentialPassengers: IPossiblePassenger[] | undefined = useMemo(() => {
    if (!isCoursePotentialPassengersCountEnabled || isWaypoint) {
      return [];
    }

    const stationRow = coursePotentialPassengers?.find(
      x => x.stationId === stationId,
    );

    if (!stationRow) {
      return [];
    }

    if (isLastStation) {
      return stationRow.passengersOut || [];
    }

    return stationRow.passengersIn || [];
  }, [
    coursePotentialPassengers,
    isCoursePotentialPassengersCountEnabled,
    isLastStation,
    isWaypoint,
    stationId,
  ]);

  const stationInOutType = useMemo(() => {
    const isBDirectionTemplate =
      (watchedCourseDirection ?? courseDto?.metadata?.direction) ===
      DirectionEnum.DropOff;

    if (isBDirectionTemplate) {
      return isFirstStation ? RideStationType.In : RideStationType.Out;
    }

    return isLastStation ? RideStationType.Out : RideStationType.In;
  }, [
    courseDto?.metadata?.direction,
    isFirstStation,
    isLastStation,
    watchedCourseDirection,
  ]);

  const showInvalidCoordinatesWarning = useMemo(() => {
    if (!station) {
      return false;
    }

    return !isLocationInfoWithValidMapCoordinates(station.locationInfo);
  }, [station]);

  return (
    <CourseStationRowStyled>
      {!isMobile && (
        <div
          className="column_grip"
          {...(isCourseStationDraggable ? { ...attributes, ...listeners } : {})}
        >
          {isCourseStationDraggable ? (
            <SVG_MUI.GripVertical3SVG className="pointer" />
          ) : (
            <DragIconReplacement />
          )}
        </div>
      )}
      {!isMobile && (
        <div className="column_passengers">
          {!isWaypoint && (
            <>
              <RideStationInOutIcon
                statusType={stationInOutType}
                isGreyed={false}
              />
              {isCoursePotentialPassengersCountEnabled && (
                <>
                  {potentialPassengers?.length ? (
                    <FramerTooltip
                      className="thin_scroll"
                      content={PotentialPassengersTooltip(potentialPassengers)}
                    >
                      <span data-e2e="course-potential-passengers">
                        {potentialPassengers.length}
                      </span>
                    </FramerTooltip>
                  ) : (
                    <span data-e2e="course-potential-passengers">0</span>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="column_map_icon">
        {!isWaypoint && (
          <FramerTooltip
            content={t("general.MANAGE_ENTITY", {
              entity: t(`station.${SINGLE_KEY}`),
            })}
          >
            <EditIcon
              className="pointer"
              onClick={handleOpenStationClick}
              color="primary"
            />
          </FramerTooltip>
        )}
      </div>

      <div className="column_station_info">
        <StationBoxLeftStyled>
          {isWaypoint ? (
            <StationRouteWaypointRowBadge
              title={t(waypointVisual.tooltipKey)}
              $category={station?.waypointCategory}
            >
              W
            </StationRouteWaypointRowBadge>
          ) : (
            realStationOrdinal
          )}
        </StationBoxLeftStyled>
        <StationBoxCenterStyled>
          <CellSimple
            color={isWaypoint ? rydeColors.midGrey : rydeColors.black}
            classNamesAdded={isWaypoint ? "waypoint-name" : ""}
          >
            {isWaypoint
              ? t("stationRoute.waypoint.ROW_LABEL", { address: stationName })
              : stationFullName}
          </CellSimple>
        </StationBoxCenterStyled>
        {timeEditAllowed && <StationCourseTimeEdit />}
        {!timeEditAllowed && (
          <StationBoxRightStyled>
            {timeArrival ? timeArrival : ZERO_TIME_HHmm}
          </StationBoxRightStyled>
        )}
      </div>
      {!isMobile && (
        <div
          className="column_area truncate-text"
          title={`${areaTitle} (${t("general.ID")}:${areaId})`}
        >
          {areaTitle}
        </div>
      )}
      <div className="column_duration">
        <div className="column_duration_text">{duration}</div>
        {showInvalidCoordinatesWarning && (
          <CourseStationWarningContentWrapperStyled>
            <FramerTooltip
              content={t("course.STATION_INVALID_MAP_COORDINATES")}
            >
              <span data-e2e="course-station-invalid-coords-warning">
                <WarningAmber className="course_station--warning" />
              </span>
            </FramerTooltip>
          </CourseStationWarningContentWrapperStyled>
        )}
      </div>
      {!isMobile && (
        <div className="column_icons">
          <StationRowButtonsCourse />
        </div>
      )}
    </CourseStationRowStyled>
  );
};
